import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Container,
  Avatar,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Person as PersonIcon } from '@mui/icons-material';
import { useEmployeeAuth } from '../hooks/useEmployeeAuth';
import LanguageSelector from './LanguageSelector';
import EmployeeNotificationBell from './EmployeeNotificationBell';
import DataMindLogo from '../assets/DataMind_Logo.png';
import AttendLogo from '../assets/attendlogo.png';

interface EmployeeLayoutProps {
  children: React.ReactNode;
}

const EmployeeLayout: React.FC<EmployeeLayoutProps> = ({ children }) => {
  const { employee, logout } = useEmployeeAuth();
  const { t } = useTranslation('auth');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box
      sx={{
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        color: 'var(--an-text-primary)',
        background:
          'radial-gradient(circle at 85% 0%, rgb(var(--an-primary-rgb) / 0.07), var(--an-transparent) 26rem), linear-gradient(180deg, var(--an-background), var(--an-surface-muted))',
      }}
    >
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          color: 'var(--an-text-primary)',
          backgroundColor: 'rgb(var(--an-surface-rgb) / 0.94)',
          backgroundImage: 'none',
          borderBottom: '1px solid var(--an-border-light)',
          boxShadow: 'var(--an-shadow-sm)',
          backdropFilter: 'blur(18px)',
          WebkitBackdropFilter: 'blur(18px)',
        }}
      >
        <Container maxWidth="xl">
          <Toolbar disableGutters sx={{ minHeight: { xs: 64, sm: 72 }, justifyContent: 'space-between', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.25, sm: 1.75 }, minWidth: 0 }}>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  px: { xs: 1, sm: 1.35 },
                  py: 0.85,
                  borderRadius: 'var(--an-radius-md)',
                  backgroundColor: 'var(--an-surface)',
                  border: '1px solid var(--an-border-light)',
                  boxShadow: 'var(--an-shadow-sm)',
                  flexShrink: 0,
                }}
              >
                <Box component="img" src={AttendLogo} alt="AttendNow" sx={{ height: { xs: 30, sm: 34 }, width: 'auto', display: 'block' }} />
              </Box>
              {!isMobile && (
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle1" noWrap sx={{ fontWeight: 800, color: 'var(--an-text-primary)', lineHeight: 1.15 }}>
                    {t('employeePortal.loginTitle')}
                  </Typography>
                  <Typography variant="caption" noWrap sx={{ color: 'var(--an-text-secondary)', fontWeight: 700 }}>
                    {employee?.emp_id || ''}
                  </Typography>
                </Box>
              )}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.75, sm: 1.25 }, minWidth: 0 }}>
              <LanguageSelector />
              <EmployeeNotificationBell />

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  minWidth: 0,
                  pl: { xs: 0, sm: 0.75 },
                }}
              >
                {!isMobile && (
                  <Box sx={{ minWidth: 0, textAlign: 'right' }}>
                    <Typography variant="body2" noWrap sx={{ fontWeight: 800, color: 'var(--an-text-primary)', maxWidth: 180 }}>
                      {employee?.name}
                    </Typography>
                    <Typography variant="caption" noWrap sx={{ color: 'var(--an-text-secondary)' }}>
                      {employee?.emp_id}
                    </Typography>
                  </Box>
                )}
                <Tooltip title={employee?.name || ''}>
                  <Avatar
                    sx={{
                      width: 36,
                      height: 36,
                      color: 'var(--an-primary)',
                      backgroundColor: 'var(--an-primary-light)',
                      border: '1px solid var(--an-border-light)',
                      boxShadow: 'var(--an-shadow-sm)',
                    }}
                  >
                    <PersonIcon fontSize="small" />
                  </Avatar>
                </Tooltip>
              </Box>

              <Button
                onClick={logout}
                startIcon={!isMobile ? <i className="bi bi-box-arrow-right"></i> : undefined}
                aria-label={t('employeePortal.logout')}
                size={isMobile ? 'small' : 'medium'}
                sx={{
                  minWidth: isMobile ? 38 : undefined,
                  width: isMobile ? 38 : undefined,
                  height: 38,
                  px: isMobile ? 0 : 1.5,
                  textTransform: 'none',
                  fontWeight: 800,
                  color: 'var(--an-text-secondary)',
                  borderRadius: 'var(--an-radius-sm)',
                  border: '1px solid var(--an-border-light)',
                  backgroundColor: 'var(--an-surface)',
                  '&:hover': {
                    color: 'var(--an-primary)',
                    backgroundColor: 'var(--an-primary-light)',
                  },
                }}
              >
                {isMobile ? <i className="bi bi-box-arrow-right"></i> : t('employeePortal.logout')}
              </Button>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      <Container maxWidth="xl" component="main" sx={{ py: { xs: 3, md: 4 }, flex: 1, width: '100%' }}>
        {children}
      </Container>

      <Box
        component="footer"
        sx={{
          py: 2.5,
          px: 2,
          mt: 'auto',
          backgroundColor: 'rgb(var(--an-surface-rgb) / 0.86)',
          borderTop: '1px solid var(--an-border-light)',
        }}
      >
        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ color: 'var(--an-text-secondary)' }}>
              Powered by
            </Typography>
            <Box component="img" src={DataMindLogo} alt="DataMind" sx={{ height: 20, width: 'auto', display: 'block' }} />
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default EmployeeLayout;


